// iter193 — 6-Month Net Profit Trend sparkline.
//
// Minimal luxury sparkline sitting beside the iter192 P&L donut. Same
// cash-basis math (server reuses `_pnl_window`), so this line can never
// disagree with the donut center number.
//
// UX rules from the user:
//   * No bars, no big graph, no axes/gridlines/legend clutter.
//   * Smooth curve, subtle emerald for profit / rose for loss.
//   * Latest month emphasized (bigger dot + label).
//   * Hover reveals "Aug 2026 · Net Profit ₹XX,XXX".
//   * Months without any transactions render "No data" (not ₹0).
//   * % change vs previous month shown only when meaningful.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const W = 260;
const H = 90;
const PAD_X = 14;
const PAD_TOP = 12;
const PAD_BOTTOM = 20;

// Simple Catmull-Rom → cubic Bezier smoothing for the trend line.
function smoothPath(points) {
  if (points.length < 2) return "";
  const p = (i) => points[Math.max(0, Math.min(points.length - 1, i))];
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = p(i - 1), p1 = p(i), p2 = p(i + 1), p3 = p(i + 2);
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

export default function ProfitTrendSparkline() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/pnl-trend?months=6")
      .then((res) => { if (!cancelled) setPoints(res.data.points || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const withData = useMemo(() => points.filter((p) => p.has_data), [points]);
  // Y-axis: symmetric around 0 so profit/loss both read fairly.
  const maxAbs = useMemo(() => {
    if (withData.length === 0) return 1;
    const m = Math.max(...withData.map((p) => Math.abs(p.net_profit)));
    return m > 0 ? m : 1;
  }, [withData]);

  const chartH = H - PAD_TOP - PAD_BOTTOM;
  const zeroY = PAD_TOP + chartH / 2;
  const scaleY = (v) => zeroY - (v / maxAbs) * (chartH / 2);

  const plotted = useMemo(() => {
    if (points.length === 0) return [];
    const n = points.length;
    const stepX = (W - PAD_X * 2) / Math.max(1, n - 1);
    return points.map((p, i) => ({
      ...p,
      x: PAD_X + stepX * i,
      y: p.has_data ? scaleY(p.net_profit) : zeroY, // no-data sits on baseline
      idx: i,
    }));
  }, [points, maxAbs]);

  const linePoints = plotted.filter((p) => p.has_data);
  const latest = plotted.length ? plotted[plotted.length - 1] : null;
  const prev = plotted.length > 1 ? plotted[plotted.length - 2] : null;
  const latestIsProfit = latest && latest.has_data ? latest.net_profit >= 0 : true;

  // % change indicator — only meaningful when both months have data
  // AND the previous month wasn't zero (dividing by 0 lies).
  let pctChange = null;
  if (latest?.has_data && prev?.has_data && Math.abs(prev.net_profit) > 1) {
    pctChange = ((latest.net_profit - prev.net_profit) / Math.abs(prev.net_profit)) * 100;
  }
  const pctPositive = pctChange !== null && pctChange >= 0;
  // Cap the visual to ±999% so a comeback month doesn't scream unrealistically.
  const pctDisplay = pctChange === null
    ? null
    : Math.abs(pctChange) > 999
      ? (pctChange > 0 ? "+999%+" : "−999%+")
      : `${pctPositive ? "+" : ""}${pctChange.toFixed(1)}%`;

  // Overall visual tone: use latest month's state.
  const lineStroke = latestIsProfit ? "rgb(4 120 87)" : "rgb(190 18 60)";
  const areaFill = latestIsProfit ? "rgba(4 120 87 / 0.08)" : "rgba(190 18 60 / 0.08)";

  const hover = hoverIdx !== null ? plotted[hoverIdx] : null;
  const hoveredIsProfit = hover?.has_data ? hover.net_profit >= 0 : true;

  // Positional line index of the latest data point (for empty-early cases).
  const firstIdx = linePoints.length ? linePoints[0].idx : null;

  return (
    <div className="rounded-lg border border-stone-200 bg-white/80 backdrop-blur-sm p-4 flex flex-col gap-3"
         data-testid="pnl-trend-sparkline">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-stone-700">
            Net profit · last 6 months
          </div>
          <div className="text-[9px] uppercase tracking-widest text-stone-400 mt-0.5">
            Are we improving?
          </div>
        </div>
        {pctChange !== null && (
          <div className={`inline-flex items-center gap-1 text-[11px] font-medium ${
            pctPositive ? "text-emerald-700" : "text-rose-700"
          }`} data-testid="pnl-trend-pct">
            {pctPositive ? <TrendingUp className="w-3 h-3" strokeWidth={2} />
                         : <TrendingDown className="w-3 h-3" strokeWidth={2} />}
            {pctDisplay}
            <span className="text-stone-400 font-normal ml-0.5">vs prev.</span>
          </div>
        )}
        {pctChange === null && !loading && (
          <div className="text-[10px] text-stone-400 italic inline-flex items-center gap-1">
            <Minus className="w-3 h-3" strokeWidth={2} /> not enough data
          </div>
        )}
      </div>

      {/* SVG chart */}
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-[90px] select-none"
          preserveAspectRatio="none"
          onMouseLeave={() => setHoverIdx(null)}
        >
          {/* Subtle zero baseline. */}
          <line
            x1={PAD_X} x2={W - PAD_X} y1={zeroY} y2={zeroY}
            stroke="rgb(214 211 209)" strokeWidth={0.7}
            strokeDasharray="3 3"
          />

          {linePoints.length >= 2 && (
            <>
              {/* Soft fill under the line — reads as a gentle sheen, not a bar. */}
              <path
                d={
                  smoothPath(linePoints) +
                  ` L ${linePoints[linePoints.length - 1].x} ${zeroY}` +
                  ` L ${linePoints[0].x} ${zeroY} Z`
                }
                fill={areaFill}
              />
              {/* Main trend line */}
              <path
                d={smoothPath(linePoints)}
                fill="none"
                stroke={lineStroke}
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          )}
          {linePoints.length === 1 && (
            <circle
              cx={linePoints[0].x} cy={linePoints[0].y} r={3}
              fill={lineStroke}
            />
          )}

          {/* Per-point dots + invisible hover targets. */}
          {plotted.map((pt, i) => {
            const isLatest = i === plotted.length - 1;
            const isProfit = pt.has_data && pt.net_profit >= 0;
            return (
              <g key={pt.month}>
                {pt.has_data && (
                  <circle
                    cx={pt.x} cy={pt.y}
                    r={isLatest ? 3.2 : 2.2}
                    fill={isProfit ? "rgb(4 120 87)" : "rgb(190 18 60)"}
                    stroke="white" strokeWidth={isLatest ? 1.4 : 1}
                    data-testid={`pnl-trend-dot-${pt.month}`}
                  />
                )}
                {!pt.has_data && (
                  <circle
                    cx={pt.x} cy={pt.y}
                    r={2}
                    fill="none" stroke="rgb(214 211 209)" strokeWidth={1}
                    strokeDasharray="1.5 1.5"
                    data-testid={`pnl-trend-nodot-${pt.month}`}
                  />
                )}
                {/* Invisible hover target — wide for touch. */}
                <rect
                  x={pt.x - 18} y={0} width={36} height={H}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHoverIdx(i)}
                  onFocus={() => setHoverIdx(i)}
                  tabIndex={0}
                  aria-label={`${pt.full_label}: ${pt.has_data ? inr(pt.net_profit) : "No data"}`}
                />
                {/* Month tick label — only for first, middle, last. */}
                {(i === 0 || i === plotted.length - 1 || i === Math.floor(plotted.length / 2)) && (
                  <text
                    x={pt.x} y={H - 4}
                    textAnchor="middle"
                    className="fill-stone-400"
                    style={{ fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase" }}
                  >
                    {pt.label.split(" ")[0]}
                  </text>
                )}
              </g>
            );
          })}

          {/* Hover vertical guide + accent dot. */}
          {hover && (
            <g pointerEvents="none">
              <line
                x1={hover.x} x2={hover.x} y1={PAD_TOP} y2={H - PAD_BOTTOM}
                stroke={hoveredIsProfit ? "rgb(4 120 87)" : "rgb(190 18 60)"}
                strokeWidth={0.6} strokeDasharray="2 2" opacity={0.6}
              />
              {hover.has_data && (
                <circle
                  cx={hover.x} cy={hover.y} r={4.5}
                  fill="none"
                  stroke={hoveredIsProfit ? "rgb(4 120 87)" : "rgb(190 18 60)"}
                  strokeWidth={1.3}
                />
              )}
            </g>
          )}
        </svg>

        {/* Floating tooltip. */}
        {hover && (
          <div
            className="absolute -top-1 pointer-events-none"
            style={{
              left: `${(hover.x / W) * 100}%`,
              transform: `translate(-50%, -100%)`,
            }}
            data-testid="pnl-trend-tooltip"
          >
            <div className="bg-stone-900 text-stone-50 rounded-md shadow-md px-2.5 py-1.5 whitespace-nowrap">
              <div className="text-[9px] uppercase tracking-[0.15em] text-stone-400">
                {hover.full_label}
              </div>
              <div className={`font-mono text-xs tabular-nums font-semibold mt-0.5 ${
                hover.has_data
                  ? (hover.net_profit >= 0 ? "text-emerald-300" : "text-rose-300")
                  : "text-stone-400 italic"
              }`}>
                {hover.has_data
                  ? `${hover.net_profit >= 0 ? "Net Profit" : "Net Loss"} ${inr(Math.abs(hover.net_profit))}`
                  : "No data"}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Latest summary line — reinforces what the eye should notice. */}
      {latest && (
        <div className="flex items-baseline justify-between pt-1 border-t border-stone-100">
          <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">
            {latest.full_label}
          </div>
          <div className={`font-mono text-xs tabular-nums font-semibold ${
            !latest.has_data ? "text-stone-400 italic"
              : (latestIsProfit ? "text-emerald-700" : "text-rose-700")
          }`} data-testid="pnl-trend-latest-value">
            {latest.has_data
              ? `${latestIsProfit ? "+" : "−"}${inr(Math.abs(latest.net_profit))}`
              : "No financial data"}
          </div>
        </div>
      )}
      {loading && (
        <div className="text-[10px] uppercase tracking-widest text-stone-400 text-center">
          Loading trend…
        </div>
      )}
    </div>
  );
}
